/**
 * P4 — HealthKit workout import.
 *
 * Runs recorded on an Apple Watch / any app writing HKWorkout appear in Tayro
 * `runs`. The import RIDES `POST /api/runs` — no new endpoint, no new table:
 * the consent/COPPA save gate + P3 route-matching + P5 idempotency all apply
 * by construction. Each workout maps to a run keyed `client_key = 'hk-'+uuid`,
 * so a re-import can never double-save (the server's partial-unique dedups).
 *
 * Native-overlap dedup (ratified 2026-07-09): the athlete may have recorded a
 * session in Tayro AND had the Watch capture it — the native run wins, so an
 * HK workout is SKIPPED when an existing Tayro run overlaps >50% of its span.
 *
 * This module is the vitest-tested pure core (mapping + overlap filter +
 * downsample) plus a native-only, no-throw driver. The pure functions carry no
 * I/O; the driver dynamic-imports the plugin so the web/TS build never depends
 * on the plugin package.
 */

import { METERS_PER_MILE, type RouteCoord } from '../run/geo';
import type { RunSavePayload } from '../run/saveQueue';
import type { AuthFetchFn } from '../../hooks/useAuthFetch';
import { authedUid } from '../authFetch';

/** One running workout from HealthKit (the plugin's queryWorkouts shape). */
export interface RawWorkout {
  uuid: string;
  startISO: string;
  endISO: string;
  durationS: number;
  distanceM: number;
  kcal?: number;
  /** [lng, lat, ele|null, tRel] samples from HKWorkoutRoute, if present. */
  route?: Array<[number, number, number | null, number]>;
}

/** The slice of an existing Tayro run the overlap dedup needs. */
export interface ExistingRunSpan {
  started_at: string;
  ended_at?: string | null;
  duration_s?: number | null;
}

export const HK_IMPORT_SOURCE = 'healthkit-import';
export const HK_KEY_PREFIX = 'hk-';
/** Native capture wins: skip an HK workout when an existing run overlaps more
 *  than this fraction of its own time span (ratified 2026-07-09). */
export const OVERLAP_SKIP_FRACTION = 0.5;
/** Match the run_capture 4-tuple point cap so import payloads stay small over
 *  the wire; the server re-downsamples + byte-guards regardless. */
const ROUTE_MAX_POINTS = 400;
/** Server pace bounds (runCapture.js). Outside → omit pace (null), never
 *  reject the whole run. */
const MIN_PACE_S_PER_MI = 120;
const MAX_PACE_S_PER_MI = 7200;

function toCoord(p: [number, number, number | null, number]): RouteCoord {
  return [p[0], p[1], p[2], p[3]];
}

/** Evenly downsample an HK route to the point cap, keeping first + last. */
export function downsampleRoute(
  route: Array<[number, number, number | null, number]>,
  maxPoints = ROUTE_MAX_POINTS,
): RouteCoord[] {
  if (route.length <= maxPoints) return route.map(toCoord);
  if (maxPoints < 2) return [toCoord(route[0])];
  const out: RouteCoord[] = [];
  const step = (route.length - 1) / (maxPoints - 1);
  for (let i = 0; i < maxPoints; i++) out.push(toCoord(route[Math.round(i * step)]));
  return out;
}

/**
 * Map one HK workout to a `POST /api/runs` payload, or null when the workout
 * is unusable (bad timestamps / negative fields). Pace is derived from
 * distance + duration and omitted when out of the server's accepted range.
 */
export function workoutToPayload(w: RawWorkout): RunSavePayload | null {
  const start = Date.parse(w.startISO);
  const end = Date.parse(w.endISO);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  if (typeof w.durationS !== 'number' || !Number.isFinite(w.durationS) || w.durationS < 0) return null;
  if (typeof w.distanceM !== 'number' || !Number.isFinite(w.distanceM) || w.distanceM < 0) return null;
  if (typeof w.uuid !== 'string' || w.uuid.length === 0) return null;

  const miles = w.distanceM / METERS_PER_MILE;
  let avgPaceSPerMi: number | null = null;
  if (miles > 0 && w.durationS > 0) {
    const pace = w.durationS / miles;
    if (pace >= MIN_PACE_S_PER_MI && pace <= MAX_PACE_S_PER_MI) {
      avgPaceSPerMi = Math.round(pace * 10) / 10;
    }
  }
  const route = w.route && w.route.length ? downsampleRoute(w.route) : null;

  return {
    clientKey: HK_KEY_PREFIX + w.uuid,
    startedAt: new Date(start).toISOString(),
    endedAt: new Date(end).toISOString(),
    durationS: Math.round(w.durationS),
    distanceM: Math.round(w.distanceM * 10) / 10,
    avgPaceSPerMi,
    // HKWorkout has no reliable aggregate ascent — derive it from the route's
    // per-point elevation (Watch routes are baro-corrected) so a hilly import
    // doesn't display "0 ft" while its own chart shows a profile (P4 review).
    // Routeless workouts stay 0 — honest, nothing to derive from.
    elevationGainM: route ? routeElevationGainM(route) : 0,
    route,
    source: HK_IMPORT_SOURCE,
  };
}

/** Sum of positive elevation deltas over consecutive ele-bearing vertices
 *  (null-ele points are skipped, not treated as zero). */
export function routeElevationGainM(route: RouteCoord[]): number {
  let gain = 0;
  let prev: number | null = null;
  for (const p of route) {
    const ele = p.length >= 3 ? (p[2] as number | null) : null;
    if (typeof ele !== 'number' || !Number.isFinite(ele)) continue;
    if (prev != null && ele > prev) gain += ele - prev;
    prev = ele;
  }
  return Math.round(gain * 10) / 10;
}

/** Fraction of a workout's own time span that an existing run covers (0..1). */
export function overlapFraction(
  w: { startISO: string; endISO: string },
  run: ExistingRunSpan,
): number {
  const wStart = Date.parse(w.startISO);
  const wEnd = Date.parse(w.endISO);
  if (!Number.isFinite(wStart) || !Number.isFinite(wEnd) || wEnd <= wStart) return 0;
  const rStart = Date.parse(run.started_at);
  let rEnd = run.ended_at ? Date.parse(run.ended_at) : NaN;
  if (!Number.isFinite(rEnd) && typeof run.duration_s === 'number' && Number.isFinite(run.duration_s)) {
    rEnd = rStart + run.duration_s * 1000;
  }
  if (!Number.isFinite(rStart) || !Number.isFinite(rEnd)) return 0;
  const overlap = Math.min(wEnd, rEnd) - Math.max(wStart, rStart);
  if (overlap <= 0) return 0;
  return overlap / (wEnd - wStart);
}

/**
 * Drop HK workouts that duplicate an existing Tayro run — >50% time-span
 * overlap with ANY existing run (native capture wins). A prior import of the
 * same workout also lands here (it overlaps itself), so this is a second guard
 * beside the `client_key` idempotency.
 */
export function filterNewWorkouts(
  workouts: RawWorkout[],
  existing: ExistingRunSpan[],
): RawWorkout[] {
  return workouts.filter(
    (w) => !existing.some((run) => overlapFraction(w, run) > OVERLAP_SKIP_FRACTION),
  );
}

// ===== opt-in (PER ACCOUNT — B57) =====

/**
 * ⚖️ FOUNDER RULING (B57, 2026-08-29): this opt-in WAS a device-global boolean
 * (`tayro.hkImport.enabled.v1`), so user A's consent left the import armed for
 * user B on the same device. Health-data consent belongs to an account, not to
 * a device. Both functions now take the account they are asking about, and the
 * store lives in `./importConsent` — rule, one-time legacy migration, and the
 * reasoning are all in that file's header.
 *
 * Re-exported here so this module's export surface is unchanged for its
 * readers; the store is React-free, so this stays a pure-core import.
 */
export {
  isImportEnabled,
  setImportEnabled,
  importConsentStorageKey,
  adoptLegacyImportConsent,
  LEGACY_IMPORT_ENABLED_KEY,
  type LegacyAdoption,
} from './importConsent';

// ===== driver (native-only, best-effort, never throws) =====

interface HealthKitWorkoutPlugin {
  requestWorkoutAuthorization(): Promise<{ granted: boolean }>;
  queryWorkouts(options: { days: number }): Promise<{ workouts: RawWorkout[] }>;
}

async function bindPlugin(): Promise<{ hk: HealthKitWorkoutPlugin } | null> {
  // ONE shared binding with H① (healthKit.ts). CRITICAL: the proxy stays
  // WRAPPED in an object across every await boundary — a Capacitor plugin
  // proxy is a thenable (its dynamic `get` manufactures a `.then` method),
  // so an async function that returns the bare proxy hangs its awaiter
  // FOREVER with zero output. That is the Live Activity lesson, and it is
  // exactly how the import froze on the founder's device 2026-07-09 (no
  // dispatch, no throw, no warn — the promise simply never settled).
  const { getHealthKitPlugin } = await import('./healthKit');
  const bound = await getHealthKitPlugin();
  return bound ? { hk: bound.hk as unknown as HealthKitWorkoutPlugin } : null;
}

export interface ImportResult {
  /** Workouts the server accepted (new saves + idempotent re-imports). */
  imported: number;
  /** Skipped as duplicates of an existing run (overlap dedup). */
  skipped: number;
  /** Running workouts HealthKit returned for the window. */
  scanned: number;
}

let inFlight = false;

/**
 * B257 — the refresh-race signal. Today's own credit reload
 * (`useWeeklyPlan`'s `loadCreditRuns`) used to fire only on mount and on the
 * NEXT foreground: an import that lands a save while Today is already open
 * had nothing telling it to look again, so the day stayed unfilled until the
 * athlete backgrounded and returned. `importHealthKitWorkouts` now notifies
 * every subscriber once it has saved at least one workout; a subscriber's own
 * error can never break the importer (caught and dropped here).
 */
type ImportSavedListener = () => void;
const importSavedListeners = new Set<ImportSavedListener>();

/** Subscribe to "this import saved at least one workout". Returns the
 *  unsubscribe function. */
export function onImportSaved(listener: ImportSavedListener): () => void {
  importSavedListeners.add(listener);
  return () => {
    importSavedListeners.delete(listener);
  };
}

function notifyImportSaved(): void {
  for (const listener of importSavedListeners) {
    try {
      listener();
    } catch {
      /* a subscriber's own error must never break the importer */
    }
  }
}

/**
 * Import the last `sinceDays` days of running workouts. Requests workout
 * authorization, queries HealthKit, drops duplicates of existing runs, POSTs
 * the survivors to /api/runs. Returns counts, or null if HealthKit is
 * unreachable / not native. Never throws — a background import must not break
 * the app (guarded like healthKit.ts / liveActivity.ts).
 */
export async function importHealthKitWorkouts(
  fetchAuthed: AuthFetchFn,
  sinceDays: number,
  /**
   * B274 — the account the CALLER already knows it is running this import
   * for (e.g. `WorkoutImportMount`'s own `useAuthContext().user.id`, read
   * synchronously before `hk.requestWorkoutAuthorization()` / `queryWorkouts()`
   * even start — no network round trip). When passed, this — not the dedupe
   * GET's own answer — is the identity the whole import runs under: an
   * account switch during those native calls, or during the dedupe GET
   * itself, is caught by `expectedUid` rather than silently adopting
   * whichever account the GET happens to answer as. `undefined` (every
   * pre-existing caller) keeps today's fallback — seeded from the dedupe
   * GET's own answer — byte-identical.
   */
  callerUid?: string | null,
): Promise<ImportResult | null> {
  if (inFlight) return null;
  const bound = await bindPlugin();
  if (!bound) return null;
  const { hk } = bound; // unwrap AFTER the await (thenable trap — see bindPlugin)
  inFlight = true;
  try {
    await hk.requestWorkoutAuthorization();
    const res = await hk.queryWorkouts({ days: sinceDays });
    const workouts = Array.isArray(res?.workouts) ? res.workouts : [];
    if (workouts.length === 0) return { imported: 0, skipped: 0, scanned: 0 };

    // Overlap dedup against the athlete's existing runs. GET /api/runs returns
    // the 50 most recent — plenty for a 30d window; the client_key partial
    // unique is the hard backstop if an older run falls outside that page.
    //
    // B234 fix2 (security review, 2026-09-26) — finding 3: seeding the
    // loop's identity from the FIRST POST's own response (below) left
    // `hk.requestWorkoutAuthorization()`, `hk.queryWorkouts()` and this GET
    // itself unguarded — a switch anywhere in that window pinned the new
    // account for the WHOLE batch, because nothing yet knew who "started"
    // this import.
    //
    // B274 — `callerUid`, when the caller passed one, IS who started this
    // import (known to the caller synchronously, before any of the above);
    // it seeds `importUid` directly and guards this GET too, so a switch
    // during `requestWorkoutAuthorization()` / `queryWorkouts()` / this GET
    // is caught rather than silently adopting whichever account the GET's
    // own answer names. No `callerUid` (every pre-existing caller) falls
    // back to today's behavior: `importUid` seeded from this GET's own
    // `authedUid` (stamped by `authFetch` on every response — the only way
    // this loop, which holds no SupabaseClient of its own, can learn who
    // just answered). Unknown (the GET itself never got a response — e.g.
    // offline) stays `undefined`: the loop below skips every item rather
    // than sending unguarded once identity truly cannot be established.
    let existing: ExistingRunSpan[] = [];
    let importUid: string | null | undefined = callerUid;
    try {
      const runsResp = await fetchAuthed(
        '/api/runs',
        importUid !== undefined ? { expectedUid: importUid } : undefined,
      );
      if (importUid === undefined) importUid = authedUid(runsResp);
      if (runsResp.ok) {
        const body = (await runsResp.json().catch(() => null)) as { runs?: ExistingRunSpan[] } | null;
        if (body && Array.isArray(body.runs)) existing = body.runs;
      }
    } catch {
      /* runs list unreachable → skip overlap filter; client_key still dedups.
         importUid stays undefined when no callerUid was passed either — the
         loop below never sends unguarded. */
    }

    const fresh = filterNewWorkouts(workouts, existing);
    let imported = 0;
    // B234 (security review, 2026-09-26) — the same class as B233: this loop
    // can span many iterations, and an in-place account switch mid-loop must
    // never post the rest of the batch into the NEW account. `importUid` was
    // seeded above, BEFORE this loop starts; every call here just passes it
    // back in as `expectedUid`, so `authFetch`'s own guard (which DOES hold
    // the SupabaseClient) checks the CURRENT session against it and refuses
    // to send — no network call — if the account has changed.
    for (const w of fresh) {
      const payload = workoutToPayload(w);
      if (!payload) continue;
      // Identity never established (the dedupe GET itself failed) — never
      // guess; skip rather than send with no guard.
      if (importUid === undefined) continue;
      try {
        const resp = await fetchAuthed('/api/runs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          expectedUid: importUid,
        });
        // 2xx (new OR idempotent dedup) counts as landed. A 4xx (consent/COPPA)
        // or 5xx is skipped silently — the next foreground pass re-scans, and
        // the client_key makes every retry safe.
        if (resp.ok) imported += 1;
      } catch {
        /* offline mid-import — the next foreground pass re-scans (idempotent) */
      }
    }
    // B257 — the refresh-race signal: at least one save landed, so any
    // subscriber (Today's credit reload) should look again right now,
    // rather than waiting for the next mount/foreground.
    if (imported > 0) notifyImportSaved();
    return { imported, skipped: workouts.length - fresh.length, scanned: workouts.length };
  } catch (err) {
    console.warn('[WorkoutImport] import failed:', err instanceof Error ? err.message : String(err));
    return null;
  } finally {
    inFlight = false;
  }
}
